/*
 * FractalAI bilingual rollout header.
 *
 * 文件定位 / File Purpose
 * - 中文：研究系统导航元数据工具，解析路由语义、主研究泳道、页面标题、推荐入口和右侧上下文。
 * - English: Research-system navigation metadata utilities for route semantics, primary lanes, page titles, recommended entries, and context rail data.
 *
 * 调用关系 / Call Flow
 * - 中文：由 app.ts/app.jsx、ResearchContextRail 和多处研究页面调用。
 * - English: Called by app.ts/app.jsx, ResearchContextRail, and multiple research pages.
 *
 * 数据与业务边界 / Data and Business Boundaries
 * - 中文：仅运行在浏览器侧或 Umi 构建侧，不直接访问数据库、不写 SQL、不复制服务端分型核心算法。
 * - English: Runs only in the browser or Umi build layer; it does not access databases, write SQL, or copy server-side fractal-core algorithms.
 *
 * 上线注意 / Release Notes
 * - 中文：作为智能研究工作台前端上线清单的一部分，修改时必须保持路由、白名单登录、报告/策略入口和生产构建校验一致。
 * - English: Part of the intelligence-workbench frontend rollout scope; keep routes, whitelist login, report/strategy entry points, and production-build checks aligned.
 */

import { buildResearchRouteMeta } from './researchNavigationRouteMeta';

export const AUTO_RESEARCH_LANE = '/intelligence/terminal';
const AUTO_RESEARCH_MENU_PATH = '/intelligence';
const DAILY_REVIEW_PATH = '/intelligence/workbench';
const COMPANY_ANALYSIS_PATH = '/company/analysis';
const ANNOUNCEMENT_REVIEW_PATH = '/intelligence/review/announcement-events';
const STRATEGY8_COVERAGE_PATH = '/intelligence/strategy8-coverage';
const RESEARCH_WATCHLISTS_PATH = '/research/watchlists';
const INDUSTRY_MAPPING_REVIEW_PATH = '/intelligence/industry-mapping-review';
const AUTO_RESEARCH_FORMAL_GROUP_KEY = 'auto-research-formal';
const AUTO_RESEARCH_SUPPORT_GROUP_KEY = 'auto-research-support';

const PRIMARY_RESEARCH_LANES = [
  {
    key: 'workspace',
    label: '智能驾驶舱',
    shortLabel: '驾驶舱',
    path: '/dashboard/workplace',
  },
  {
    key: 'market',
    label: '全市场行情',
    shortLabel: '市场',
    path: '/market/overview',
  },
  {
    key: 'research',
    label: '个股研究',
    shortLabel: '研究',
    path: '/research/symbol',
  },
  {
    key: 'strategy',
    label: '策略与报告',
    shortLabel: '策略',
    path: '/strategy/library',
  },
  {
    key: 'intelligence',
    label: '自动研究所',
    shortLabel: '研究所',
    path: AUTO_RESEARCH_LANE,
  },
];

const isAutoResearchRootItem = item => (
  item?.path === DAILY_REVIEW_PATH
  || item?.path === COMPANY_ANALYSIS_PATH
  || item?.path === STRATEGY8_COVERAGE_PATH
  || item?.path === RESEARCH_WATCHLISTS_PATH
  || item?.path === INDUSTRY_MAPPING_REVIEW_PATH
);

const buildAutoResearchChild = (sourceItem, {
  groupKey,
  key,
  name,
  path,
}) => {
  const { parentKeys: ignoredParentKeys, ...source } = sourceItem || {};
  void ignoredParentKeys;
  return {
    ...source,
    name,
    path,
    key,
    parentKeys: [AUTO_RESEARCH_MENU_PATH, groupKey],
  };
};

export const buildResearchMenuData = (menuData = []) => {
  if (!Array.isArray(menuData)) return [];

  const dailyReviewItem = menuData.find(item => item?.path === DAILY_REVIEW_PATH);
  const companyAnalysisItem = menuData.find(item => item?.path === COMPANY_ANALYSIS_PATH);
  const strategy8CoverageItem = menuData.find(item => item?.path === STRATEGY8_COVERAGE_PATH);
  const researchWatchlistsItem = menuData.find(item => item?.path === RESEARCH_WATCHLISTS_PATH);
  const industryMappingReviewItem = menuData.find(item => item?.path === INDUSTRY_MAPPING_REVIEW_PATH);
  if (!dailyReviewItem && !companyAnalysisItem && !strategy8CoverageItem &&
      !researchWatchlistsItem && !industryMappingReviewItem) return menuData;

  const firstSourceIndex = Math.min(
    ...[
      dailyReviewItem,
      companyAnalysisItem,
      strategy8CoverageItem,
      researchWatchlistsItem,
      industryMappingReviewItem,
    ]
      .filter(Boolean)
      .map(item => menuData.indexOf(item)),
  );
  const rootItems = menuData.filter(item => !isAutoResearchRootItem(item));
  const insertAt = menuData
    .slice(0, firstSourceIndex)
    .filter(item => !isAutoResearchRootItem(item))
    .length;
  const autoResearchMenu = {
    name: '自动研究所',
    path: AUTO_RESEARCH_MENU_PATH,
    key: AUTO_RESEARCH_MENU_PATH,
    icon: dailyReviewItem?.icon || companyAnalysisItem?.icon,
    children: [
      {
        name: '正式研究',
        key: AUTO_RESEARCH_FORMAL_GROUP_KEY,
        children: [
          buildAutoResearchChild(null, {
            groupKey: AUTO_RESEARCH_FORMAL_GROUP_KEY,
            name: '研究所总屏',
            path: AUTO_RESEARCH_LANE,
            key: AUTO_RESEARCH_LANE,
          }),
          buildAutoResearchChild(strategy8CoverageItem, {
            groupKey: AUTO_RESEARCH_FORMAL_GROUP_KEY,
            name: '首次覆盖',
            path: STRATEGY8_COVERAGE_PATH,
            key: STRATEGY8_COVERAGE_PATH,
          }),
          buildAutoResearchChild(researchWatchlistsItem, {
            groupKey: AUTO_RESEARCH_FORMAL_GROUP_KEY,
            name: '持续跟踪',
            path: RESEARCH_WATCHLISTS_PATH,
            key: RESEARCH_WATCHLISTS_PATH,
          }),
        ],
      },
      {
        name: '辅助工具',
        key: AUTO_RESEARCH_SUPPORT_GROUP_KEY,
        children: [
          buildAutoResearchChild(dailyReviewItem, {
            groupKey: AUTO_RESEARCH_SUPPORT_GROUP_KEY,
            name: '交易复盘',
            path: DAILY_REVIEW_PATH,
            key: DAILY_REVIEW_PATH,
          }),
          buildAutoResearchChild(companyAnalysisItem, {
            groupKey: AUTO_RESEARCH_SUPPORT_GROUP_KEY,
            name: '公告新闻稿',
            path: COMPANY_ANALYSIS_PATH,
            key: COMPANY_ANALYSIS_PATH,
          }),
          buildAutoResearchChild(industryMappingReviewItem, {
            groupKey: AUTO_RESEARCH_SUPPORT_GROUP_KEY,
            name: '行业映射',
            path: INDUSTRY_MAPPING_REVIEW_PATH,
            key: INDUSTRY_MAPPING_REVIEW_PATH,
          }),
        ],
      },
    ],
  };

  return [
    ...rootItems.slice(0, insertAt),
    autoResearchMenu,
    ...rootItems.slice(insertAt),
  ];
};

const SOURCE_LABELS = {
  market: '市场扫描',
  watchlist: '自选股 · 持续跟踪',
  lightweight: '高性能图表工作台',
};

const ROUTE_META = buildResearchRouteMeta(AUTO_RESEARCH_LANE);

const matchRouteMeta = (pathname) =>
  ROUTE_META.find((item) => (item.exact ? item.path === pathname : pathname.startsWith(item.path)));

const safeDecode = (value) => {
  try {
    return decodeURIComponent(value || '');
  } catch (error) {
    return value || '';
  }
};

const buildPathSegmentMatcher = (pathname = '', prefix) => {
  if (!pathname.startsWith(prefix)) {
    return null;
  }

  const remainder = pathname.slice(prefix.length);

  if (!remainder) {
    return null;
  }

  return safeDecode(remainder.split('/')[0]);
};

export const extractResearchRouteContext = (pathname = '', search = '') => {
  const normalizedPath = pathname || '/';
  const params = new URLSearchParams(search || '');
  const staticReportRoutes = ['/reports/list', '/reports/archive', '/reports/hotspot', '/reports/attribution'];
  const staticStrategyRoutes = [
    '/strategy/library',
    '/strategy/import',
    '/strategy/signals',
    '/strategy/signals/breakoutwatch',
    '/strategy/signals/uptrend-double-resistance',
    '/strategy/signals/valid-wave-pivot',
  ];
  const querySymbol = params.get('symbol');
  const source = params.get('source');
  const compareLeft = params.get('left');
  const compareRight = params.get('right');
  const routeSymbol = buildPathSegmentMatcher(normalizedPath, '/research/symbol/');
  const indexSymbol = buildPathSegmentMatcher(normalizedPath, '/research/index/');
  const category = buildPathSegmentMatcher(normalizedPath, '/reports/category/');
  const reportId = normalizedPath.startsWith('/reports/category/') || staticReportRoutes.includes(normalizedPath)
    ? null
    : buildPathSegmentMatcher(normalizedPath, '/reports/');
  const strategyId = staticStrategyRoutes.includes(normalizedPath)
    ? null
    : buildPathSegmentMatcher(normalizedPath, '/strategy/');

  return {
    symbol: routeSymbol || querySymbol || null,
    source,
    compareLeft,
    compareRight,
    indexSymbol,
    category,
    reportId,
    strategyId,
  };
};

const buildContextLabel = (pathname, search) => {
  const params = new URLSearchParams(search || '');
  const symbol = params.get('symbol');
  const source = params.get('source');

  if (symbol) {
    const sourceLabel = source ? SOURCE_LABELS[source] || source : null;
    return sourceLabel ? `当前标的 ${symbol} · 来源 ${sourceLabel}` : `当前标的 ${symbol}`;
  }

  return null;
};

const buildEnhancedContextLabel = (pathname, search) => {
  const {
    symbol,
    source,
    compareLeft,
    compareRight,
    indexSymbol,
    category,
    reportId,
    strategyId,
  } = extractResearchRouteContext(pathname, search);

  if (compareLeft || compareRight) {
    const left = compareLeft || '左侧对象';
    const right = compareRight || '右侧对象';
    return `${left} vs ${right}`;
  }

  if (symbol) {
    const sourceLabel = source ? SOURCE_LABELS[source] || source : null;
    return sourceLabel ? `当前标的 ${symbol} · 来源 ${sourceLabel}` : `当前标的 ${symbol}`;
  }

  if (indexSymbol) {
    return `指数对象 ${indexSymbol}`;
  }

  if (category) {
    return `报告分类 ${category}`;
  }

  if (reportId) {
    return `报告对象 ${reportId}`;
  }

  if (strategyId) {
    return `策略对象 ${strategyId}`;
  }

  return buildContextLabel(pathname, search);
};

export const getPrimaryResearchLanes = () => PRIMARY_RESEARCH_LANES;

export const resolveResearchRouteMeta = (pathname = '', search = '') => {
  const normalizedPath = pathname || '/';
  const matchedRoute = matchRouteMeta(normalizedPath);

  if (!matchedRoute) {
    return null;
  }

  const routeContext = extractResearchRouteContext(normalizedPath, search);
  const contextLabel = buildEnhancedContextLabel(normalizedPath, search);

  return {
    ...matchedRoute,
    routeContext,
    contextLabel,
  };
};

export const buildResearchDocumentTitle = (pathname = '', search = '') => {
  const routeMeta = resolveResearchRouteMeta(pathname, search);

  if (!routeMeta) {
    return 'FractalAI';
  }

  const contextTitle = routeMeta.contextLabel ? ` · ${routeMeta.contextLabel}` : '';
  return `${routeMeta.title}${contextTitle} | FractalAI`;
};
